import tfaStatuses from '@/constants/tfa-status';
import API_AUTH_LOGIN from '@/services/api/authLogin.js';
import API_LOGIN from '@/services/api/login.js';
import API_LOGOUT from '@/services/api/logout';
import API_PROFILE from '@/services/api/profile.js';
import API_TFA from '@/services/api/twoFactorAuthenticatication.js';
import { deleteAllIndexedDB, isIndexedDBAvailable } from '@/utils/indexedDB';
import axios from 'axios';
import staticDataAction from './static-data';
import { clearTTDataAndFile } from './trade-trust';
import csatKeys from '@/constants/configuration/csat-keys.json';
import API_DEX from '@/services/api/dex.js';
import { getEnvAndDex } from '@/utils/get-env-and-dex';
import API_RENEW_TOKEN from '@/services/api/renewToken.js';
import { useTradex } from '@/hooks/use-tradex/index';

const { env } = getEnvAndDex();

const SHELL_HOSTED = process.env.REACT_APP_SHELL_HOSTED === 'true';

function handleTfa(user) {
	const { tfaRequired, tfaUserSetup, tfaVerified } = user

	let tfaStatus = tfaStatuses.none

	if (user?.isAdmin) {
		return tfaStatus = tfaStatuses.none
	}

	if (tfaVerified) {
		return tfaStatus = tfaStatuses.none
	}

	if (tfaUserSetup) {
		return tfaStatus = tfaStatuses.otp
	}

	if (tfaRequired) {
		return tfaStatus = tfaStatuses.setUp
	}

	return tfaStatus = tfaStatuses.none
}

async function handleUser(response) {
	const { user } = response

	let profile = [];

	if ((!user?.tfaRequired) || (user?.tfaRequired && user?.tfaVerified)) {
		profile = (await API_PROFILE.getProfile(user.id))[1]
	}

	return {
		profile,
	}
}

function login(goTo, credentials, flow) {

	return async (dispatch, getState) => {
		const version = getState().version.data;
		const isTradex = useTradex();
		let error, response

		switch (flow) {
			case "setUpTfa":
				[error, response] = await API_TFA.verifyTfa({ ...credentials })
				break;
			case "otpVerify":
				[error, response] = await API_TFA.validateTfa({ ...credentials })
				break;
			case "authLogin":
				[error, response] = await API_AUTH_LOGIN.create({ ...credentials })
				break;
			default:
				[error, response] = await API_LOGIN.create({ ...credentials, email: credentials.email.toLowerCase() })
				break;
		}

		if (error) {
			if (!isTradex && error.message.includes("Unable to retrieve user from DB")) {
				throw new Error("Login failed. Please check your credentials.<br/>For further assistance, click the 'Get Help' widget on the right to contact support.");
			}
			if (error.message === 'Invalid credentials.' || error.message === 'Failed to Fetch User Details' || error.message === 'Invalid Login Credentials' || (String(error.message.toLowerCase()).includes("status code") && String(error.message.toLowerCase()).includes("400"))) {
				throw new Error('credentialError')
			}
			if (String(error.message.toLowerCase()).includes("status code") && !String(error.message.toLowerCase()).includes("400")) {
				throw new Error(!isTradex ? "Login failed. Please check your credentials.<br/>For further assistance, click the 'Get Help' widget on the right to contact support."
					: "An error occured! Please contact administrator.");
			}
			throw new Error(error.message)
		}

		const { token, systemId, user } = response

		const prevAuth = axios.defaults.headers.common['Authorization']
		axios.defaults.headers.common['Authorization'] = `Bearer ${token}`

		const { profile } = await handleUser(response)
		const tfaValue = handleTfa(user)

		// /v1/dex needs the session token (CTD-14491), so it is fetched only after login
		// succeeds. While 2FA is pending the token is not verified yet; login() runs again
		// after the OTP step and loads the dex config then (tfaGuard holds the user meanwhile).
		let dexResponse = {}
		if (tfaValue === tfaStatuses.none) {
			const [dexError, dexData] = await API_DEX.index();
			if (dexError) {
				// restore, not delete: an OTP retry still needs the pending 2FA token
				axios.defaults.headers.common['Authorization'] = prevAuth
				throw new Error("Failed to load dex config");
			}
			dexResponse = dexData || {}
		}

		const { features, apiSpecUrl } = dexResponse;
		const featuresList = features || [];

		sessionStorage.setItem('pitstopCoreToken', token)
		sessionStorage.setItem('user', JSON.stringify(user))
		sessionStorage.setItem('systemId', systemId)
		sessionStorage.setItem('profile', JSON.stringify(profile))
		sessionStorage.setItem('tfa', tfaValue)
		sessionStorage.setItem('features', JSON.stringify(featuresList))
		sessionStorage.setItem('apiSpecUrl', apiSpecUrl || '');
		if (featuresList.includes('SUBMITTALS') && dexResponse.submittalsHost) {
			sessionStorage.setItem('submittalsHost', dexResponse.submittalsHost);
		}

		if (isIndexedDBAvailable()) {
			deleteAllIndexedDB()
		}

		axios.defaults.headers.common['Authorization'] = `Bearer ${token}`

		dispatch({
			type: 'auth/login',
			payload: response,
			profile: profile,
			tfa: tfaValue,
			features: featuresList,
			apiSpecUrl: apiSpecUrl,
		})

		if (version.status === 'UPDATE_AVAILABLE') {
			sessionStorage.setItem("banner", true)
		}

		goTo(`/`)
	}
}

// Drop the local session: token store, the TradeTrust IndexedDB caches, axios auth
// header, and the CSAT-related localStorage keys. Shared by shell and legacy paths.
// Takes `dispatch` because the TradeTrust cleanup is a redux action.
function clearLocalSession(dispatch) {
	sessionStorage.clear();
	if (isIndexedDBAvailable()) {
		dispatch(clearTTDataAndFile());
		deleteAllIndexedDB();
	}
	delete axios.defaults.headers.common['Authorization'];
	localStorage.removeItem('userEmail');
	localStorage.removeItem('lockedTime');
	localStorage.removeItem('lockedAccount');
	localStorage.removeItem('isTableView');
	localStorage.removeItem('isListView');
}

// Remove the injected CSAT widget script, if present.
function removeCsatWidget() {
	const csatWidgetBaseUrl = csatKeys[env]?.CSAT_WIDGET_BASE_URL;
	const csatSpaceKey = csatKeys[env]?.CSAT_SPACE_KEY;
	const selector = `script[src="${csatWidgetBaseUrl}${csatSpaceKey}?onload=onUserSnapLoad"]`;
	if (document.querySelector(selector)) {
		document.querySelector(selector).remove();
	}
}

// Best-effort server-side logout for BOTH surfaces (a single shell tab holds both
// tokens). Fired keepalive + fire-and-forget so the requests survive the full-page
// redirect without blocking on slow in-flight requests. Only flips each token's
// `revoked` flag (pitstop also logs an audit entry). Read tokens before clear().
function postLogoutKeepalive() {
	const pitstopToken = sessionStorage.getItem('pitstopCoreToken');
	const pitstopHostname = sessionStorage.getItem('pitstopHostname');
	const adminToken = sessionStorage.getItem('adminCoreToken');
	const dex = sessionStorage.getItem('selectedDex');
	if (pitstopToken) {
		fetch('/api/logout', {
			method: 'POST',
			keepalive: true,
			headers: {
				Authorization: `Bearer ${pitstopToken}`,
				'X-Request-From': 'pitstop',
				...(pitstopHostname ? { 'X-Pitstop-Hostname': pitstopHostname } : {}),
			},
		}).catch(() => {});
	}
	if (adminToken) {
		fetch('/api/v1/logout', {
			method: 'POST',
			keepalive: true,
			headers: { Authorization: `Bearer ${adminToken}`, 'X-Request-From': 'admin', ...(dex ? { dex } : {}) },
		}).catch(() => {});
	}
}

function logout(goTo) {
	return async (dispatch) => {
		dispatch(staticDataAction.removeStaticDataAction())

		// Unified portal: fire both surfaces' /logout (keepalive), wipe the whole
		// local session, and full-page redirect to the portal /login. No auth/logout
		// dispatch — the full-page nav destroys the redux store anyway, and skipping
		// it avoids re-running the route guards on a null user mid-teardown.
		if (SHELL_HOSTED) {
			postLogoutKeepalive();
			clearLocalSession(dispatch);
			removeCsatWidget();
			window.location.assign('/login');
			return;
		}

		// Legacy (standalone): await own-surface logout, wipe session, reset redux,
		// then in-app navigate + reload. Unchanged behavior.
		await API_LOGOUT.create();
		clearLocalSession(dispatch);
		removeCsatWidget();
		dispatch({ type: 'auth/logout' });
		goTo('/login');
		window.location.reload();
	}
}

// Shell-hosted bootstrap. The portal shell already obtained a pitstop-core
// token via dual-login and put it in sessionStorage. Here we replicate the
// post-token side of the standard login() action so 'profile', 'features',
// 'apiSpecUrl', 'tfa', and the auth/login Redux state are populated. Without
// this the in-app `use-config` hook (gated on profile?.tncStatus) never
// fetches /v1/config and the page sits blank.
function hydrateShellSession() {
	return async (dispatch) => {
		const token = sessionStorage.getItem('pitstopCoreToken');
		if (!token) return;
		axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;

		// Refresh the token + grab the current user record. renew-token is the
		// cheapest endpoint that returns a fresh user; reuse the cached one
		// from sessionStorage if renew-token fails so we still hydrate the
		// rest of the session.
		let user = null;
		try {
			const userStr = sessionStorage.getItem('user');
			if (userStr) user = JSON.parse(userStr);
		} catch (_) {
			user = null;
		}
		try {
			const [renewErr, renewRes] = await API_RENEW_TOKEN.create(null, {
				headers: { Authorization: `Bearer ${token}` }
			});
			if (!renewErr && renewRes) {
				const renewedToken = renewRes.token || token;
				user = renewRes.user || user;
				axios.defaults.headers.common['Authorization'] = `Bearer ${renewedToken}`;
				sessionStorage.setItem('pitstopCoreToken', renewedToken);
				if (user) sessionStorage.setItem('user', JSON.stringify(user));
				if (user?.systems?.[0]?.systemId) {
					sessionStorage.setItem('systemId', user.systems[0].systemId);
				}
			}
		} catch (_) { /* fall through with cached user */ }

		if (!user) return; // truly no user info — let the next 401 push to /login

		// Features, apiSpecUrl, and profile come from sessionStorage (portal pre-fetched at login).
		// shouldHydrate in src/index.js redirects to /login if any are missing.
		const featuresStr = sessionStorage.getItem('features');
		const features = featuresStr ? JSON.parse(featuresStr) : [];
		const apiSpecUrl = sessionStorage.getItem('apiSpecUrl') || '';

		let profile = [];
		try {
			if ((!user?.tfaRequired) || (user?.tfaRequired && user?.tfaVerified)) {
				const profileStr = sessionStorage.getItem('profile');
				profile = profileStr ? JSON.parse(profileStr) : [];
			}
		} catch (_) { /* leave profile empty — guards will see it */ }

		const tfaValue = handleTfa(user);

		sessionStorage.setItem('profile', JSON.stringify(profile));
		sessionStorage.setItem('tfa', tfaValue);
		sessionStorage.setItem('features', JSON.stringify(features));
		sessionStorage.setItem('apiSpecUrl', apiSpecUrl);
		// Tag which app last hydrated, so cross-app hops re-hydrate. See index.js shouldHydrate.
		sessionStorage.setItem('profileSurface', 'pitstop');

		dispatch({
			type: 'auth/login',
			payload: { token: sessionStorage.getItem('pitstopCoreToken'), user, systemId: sessionStorage.getItem('systemId') || '' },
			profile,
			tfa: tfaValue,
			features,
			apiSpecUrl
		});
	};
}

function renewToken() {
	return async (dispatch) => {
		const currentToken = sessionStorage.getItem("pitstopCoreToken");
			const [error, response] = await API_RENEW_TOKEN.create(null, {
				headers: {
					Authorization: `Bearer ${currentToken}`,
				},
			});
			if (error) throw new Error(error.message);
			const { token, user } = response;
			axios.defaults.headers.common["Authorization"] = `Bearer ${token}`;
			sessionStorage.setItem("pitstopCoreToken", token);
			sessionStorage.setItem("user", JSON.stringify(user));
			dispatch({
				type: "auth/renewToken",
				payload: { token, user },
			});
	};
}

export {
	login,
	logout,
	renewToken,
	hydrateShellSession
};

